import {useAppOverrides} from 'contexts/AppOverridesContext';
import {getAppearanceSettingsRef} from 'contexts/ColorSchemeContext';
import {setDoc} from 'firebase/firestore';
import {useAuthUid} from 'hooks/user/useUserId';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {useDocumentData} from 'react-firebase-hooks/firestore';
import {z} from 'zod';

/** The id of the module-card list every course has always had. */
export const CLASSIC_ROADMAP_STYLE = 'classic';

/**
 * A student-home roadmap layout the user can choose: the classic list, or
 * one of the views a deploy supplies through `AppOverrides.roadmapViews`.
 * Ids are validated against the available options on read, so an id from a
 * view the deploy no longer offers falls back to classic.
 */
export type RoadmapStyle = string;

/** One entry in the roadmap layout picker. */
export interface RoadmapStyleOption {
  id: RoadmapStyle;
  /** Short label for the settings select and the in-page switcher. */
  label: string;
}

/**
 * The slice of the per-user appearance settings document this context owns.
 * Other contexts own other fields of the same document; each parses only its
 * own, leniently, so an unknown value falls back to the default.
 */
const RoadmapAppearanceSchema = z.object({
  roadmapStyle: z.string().optional(),
});

/** Value exposed by {@link RoadmapStyleProvider}. */
export interface RoadmapStyleContextData {
  /**
   * The layout in effect: the stored choice while the choice is offered,
   * else the deploy default.
   */
  roadmapStyle: RoadmapStyle;
  /** Every choosable layout, classic first, in the deploy's order. */
  options: readonly RoadmapStyleOption[];
  /**
   * Whether the deploy offers a choice: it must both allow it
   * (`AppOverrides.hasRoadmapStyleSetting`) and supply at least one view
   * (`AppOverrides.roadmapViews`). When false the controls are hidden and the
   * deploy default is used regardless of any stored choice.
   */
  isChoiceAvailable: boolean;
  /**
   * Stores a new choice for the signed-in user. A no-op (logged at debug
   * level) when no user is signed in.
   */
  setRoadmapStyle: (style: RoadmapStyle) => void;
  /**
   * Where the student last scrolled each roadmap view in this tab, as the
   * page's scroll position relative to the top of the view, keyed by the
   * view. Mutated in place, so writes do not re-render; empty again after a
   * reload.
   */
  scrollOffsets: Map<string, number>;
}

const CLASSIC_OPTION: RoadmapStyleOption = {
  id: CLASSIC_ROADMAP_STYLE,
  label: 'Classic',
};

const RoadmapStyleContext = createContext<RoadmapStyleContextData>({
  roadmapStyle: CLASSIC_ROADMAP_STYLE,
  options: [CLASSIC_OPTION],
  isChoiceAvailable: false,
  setRoadmapStyle: () => {},
  scrollOffsets: new Map(),
});

/**
 * Resolves the student-home roadmap layout for the signed-in user.
 *
 * The choice is read live from the user's appearance settings document (the
 * one the colour scheme and other UI preferences live in), so it follows the
 * user across devices. Until it loads, or when the deploy offers no choice,
 * the deploy default (`AppOverrides.defaultRoadmapStyle`) applies; a default
 * that names no available view falls back to classic. Consumed by the student
 * home and the settings page.
 */
export function RoadmapStyleProvider({children}: {children: ReactNode}) {
  const {hasRoadmapStyleSetting, defaultRoadmapStyle, roadmapViews} =
    useAppOverrides();
  const uid = useAuthUid();
  const settingsRef = useMemo(() => getAppearanceSettingsRef(uid), [uid]);
  const [settingsData] = useDocumentData(settingsRef);
  // Local echo of the last write so the home switches immediately rather
  // than waiting for the Firestore listener to deliver the stored value.
  const [pendingStyle, setPendingStyle] = useState<RoadmapStyle | null>(null);
  const [scrollOffsets] = useState(() => new Map<string, number>());

  const options = useMemo<RoadmapStyleOption[]>(
    () => [
      CLASSIC_OPTION,
      ...(roadmapViews ?? []).map(view => ({id: view.id, label: view.label})),
    ],
    [roadmapViews],
  );

  const storedStyle = useMemo(() => {
    const parsed = RoadmapAppearanceSchema.safeParse(settingsData ?? {});
    return parsed.success ? parsed.data.roadmapStyle : undefined;
  }, [settingsData]);

  const isChoiceAvailable = Boolean(
    hasRoadmapStyleSetting && roadmapViews && roadmapViews.length > 0,
  );
  const deployDefault = defaultRoadmapStyle ?? CLASSIC_ROADMAP_STYLE;
  const chosen = isChoiceAvailable
    ? (pendingStyle ?? storedStyle ?? deployDefault)
    : deployDefault;
  const roadmapStyle: RoadmapStyle = options.some(
    option => option.id === chosen,
  )
    ? chosen
    : CLASSIC_ROADMAP_STYLE;

  const setRoadmapStyle = useCallback(
    (next: RoadmapStyle) => {
      setPendingStyle(next);
      if (!settingsRef) {
        console.debug('[RoadmapStyle] no signed-in user; choice not stored');
        return;
      }
      setDoc(settingsRef, {roadmapStyle: next}, {merge: true}).catch(e => {
        console.debug('[RoadmapStyle] failed to persist choice', next, e);
      });
    },
    [settingsRef],
  );

  const value = useMemo<RoadmapStyleContextData>(
    () => ({
      roadmapStyle,
      options,
      isChoiceAvailable,
      setRoadmapStyle,
      scrollOffsets,
    }),
    [roadmapStyle, options, isChoiceAvailable, setRoadmapStyle, scrollOffsets],
  );

  return (
    <RoadmapStyleContext.Provider value={value}>
      {children}
    </RoadmapStyleContext.Provider>
  );
}

/**
 * Returns the active roadmap-style state.
 *
 * @return The nearest {@link RoadmapStyleContextData}. Without a provider,
 *     returns the classic layout with no choice available and a no-op setter.
 * @throws Never.
 */
export function useRoadmapStyle(): RoadmapStyleContextData {
  return useContext(RoadmapStyleContext);
}
